<template>
  <div>
    <div :class="stepClass">
      <div class="columns is-multiline">
        <div class="column is-full">
          <input-custom
            id="address-1"
            v-model.lazy="$v.value.addressLine1.$model"
            stop-clearfix
            :input-class="`input ${hasErrorClass($v.value.addressLine1)}`"
            type="text"
            :label="$t('business_listing.stepper.address')"
            :error-text="errorGenerator($v.value.addressLine1)"
            @input="delayTouch($v.value.addressLine1)"
          />
        </div>
        <div class="column is-full">
          <input-custom
            id="address-2"
            v-model.lazy="$v.value.addressLine2.$model"
            stop-clearfix
            :input-class="`input ${hasErrorClass($v.value.addressLine2)}`"
            type="text"
            :error-text="errorGenerator($v.value.addressLine2)"
            @input="delayTouch($v.value.addressLine2)"
          />
        </div>
      </div>
      <div class="columns">
        <div class="column is-half-desktop is-full-tablet">
          <custom-input
            id="country"
            v-model.lazy="$v.value.countryCode.$model"
            stop-clearfix
            :input-class="`input ${hasErrorClass($v.value.countryCode)}`"
            :label-as-a-container="false"
            :label="$t('common.country')"
            :error-text="errorGenerator($v.value.countryCode)"
            @input="delayTouch($v.value.countryCode)"
          >
            <multiselect
              v-model="country"
              :options="countries"
              :multiple="false"
              :placeholder="$t('common.select_country')"
              :close-on-select="true"
              track-by="code"
              label="name"
              :allow-empty="false"
              :show-labels="false"
              :searchable="true"
            />
          </custom-input>
        </div>
        <div class="column is-half-desktop is-full-tablet">
          <input-custom
            v-show="!isCountryUS"
            id="province"
            v-model="value.province"
            stop-clearfix
            :input-class="`input ${hasErrorClass($v.value.province)}`"
            type="text"
            :label="$t('business_listing.stepper.province')"
            :error-text="errorGenerator($v.value.province)"
            @input="delayTouch($v.value.province)"
          />
          <custom-input
            v-show="isCountryUS"
            id="state"
            v-model="$v.value.state.$model"
            stop-clearfix
            :label-as-a-container="false"
            :input-class="`input ${hasErrorClass($v.value.state)}`"
            :label="$t('business_listing.stepper.state')"
            :error-text="errorGenerator($v.value.state)"
            @input="delayTouch($v.value.state)"
          >
            <multiselect
              v-model="state"
              :options="ustates"
              :multiple="false"
              :placeholder="$t('common.select_state')"
              :close-on-select="true"
              track-by="code"
              label="name"
              :allow-empty="false"
              :show-labels="false"
              :searchable="true"
            />
          </custom-input>
        </div>
      </div>
      <div class="columns">
        <div class="column is-half-desktop is-full-tablet">
          <input-custom
            id="city"
            v-model.lazy="$v.value.city.$model"
            stop-clearfix
            :input-class="`input ${hasErrorClass($v.value.city)}`"
            type="text"
            :label="$t('business_listing.stepper.city')"
            :error-text="errorGenerator($v.value.city)"
            @input="delayTouch($v.value.city)"
          />
        </div>
        <div class="column is-half-desktop is-full-tablet">
          <input-custom
            id="postcode"
            v-model.lazy="$v.value.postCode.$model"
            stop-clearfix
            :input-class="`input ${hasErrorClass($v.value.postCode)}`"
            type="text"
            :label="$t('business_listing.stepper.postcode')"
            :error-text="errorGenerator($v.value.postCode)"
            @input="delayTouch($v.value.postCode)"
          />
        </div>
      </div>
      <CustomSwitch
        v-model="value.hideAddress"
        :label="$t('business_listing.stepper.hide_address')"
      />
    </div>
  </div>
</template>

<script>
import CustomInput from '@/components/Shared/CustomInput'
import InputCustom from '@/components/Shared/Form/InputCustom'
import CustomSwitch from '@/components/Shared/Switch'
import Multiselect from 'vue-multiselect'
import formHelperMixin from '@/mixins/form-helper'
import { helpers } from 'vuelidate/lib/validators'
import extraConstants from '@/helpers/form-validators.js'

const minLenghtOptional = (limit) => helpers.withParams(
  { type: 'minLenghtOptional', min: limit },
  (value) => {
    if (value.length === 0) {
      return true
    }
    if (value.length > limit) {
      return true
    }
    return false
  })

export default {

  name: 'StepAddress',
  components: {
    CustomInput,
    InputCustom,
    Multiselect,
    CustomSwitch
  },
  mixins: [formHelperMixin],
  props: {
    // class can't be used as a prop name, so to pass any custom classes as a
    // prop, we need to pass them in as 'stepClass'
    stepClass: {
      type: String,
      default: ''
    },
    // Check if this step is the first or last and therefore decide whether to
    // show or hide the next/prev buttons
    isOuterStep: {
      type: Object,
      default: () => ({ first: true, last: false })
    },
    // The value should be passed in as a prop, and we can use the v-model
    // directive to bind to the specific part of the passed value relevant
    // for this step
    value: {
      type: Object,
      default: () => ({
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        countryCode: '',
        state: '',
        city: '',
        province: '',
        postcode: '',
        hideAddress: false,
        hasError: false
      })
    }
  },
  validations: {
    value: {
      addressLine1: {
        minLenghtOptional: minLenghtOptional(3)
      },
      addressLine2: {
      },
      countryCode: {
      },
      city: {
        minLenghtOptional: minLenghtOptional(2)
      },
      state: {
      },
      province: {
      },
      postCode: {
        minLenghtOptional: minLenghtOptional(2)
      },
      hideAddress: {
      }
    }
  },
  data () {
    return {
      country: {
        name: '',
        code: ''
      },
      state: {
        name: '',
        code: ''
      },
      countries: [],
      ustates: extraConstants.USSTATESLIST
    }
  },
  computed: {
    isCountryUS () {
      if (this.country.code === 'US') {
        return true
      }
      return false
    },
    hasSelectedCountry () {
      if (this.country.code !== '') {
        return true
      }
      return false
    }
  },
  watch: {
    $v: {
      handler (obj) {
        this.value.hasError = this.$v.value.$anyError
      },
      deep: true
    },
    country: {
      handler (obj) {
        this.value.countryCode = this.country.code
      },
      deep: true
    },
    state: {
      handler (obj) {
        this.value.state = this.state.code
      },
      deep: true
    },
    value: {
      handler (obj) {
        if (this.value.countryCode !== '' &&
            this.value.countryCode !== this.country.code) {
          this.getCountrySelect()
        }
        if (this.value.state !== '' &&
            this.value.state !== this.ustates.code) {
          this.getStateSelect()
        }
      },
      deep: true
    }
  },

  beforeMount () {
    this.translateCountries()
  },

  methods: {
    step ({ target }) {
      if (target.id === 'next-btn') {
        this.$emit('next', this.$v)
      } else if (target.id === 'prev-btn') {
        this.$emit('prev')
      }
    },
    getCountrySelect () {
      var index = 0
      var found = false
      while (!found && index < this.countries.length) {
        if (this.countries[index].code === this.value.countryCode) {
          this.country.code = this.countries[index].code
          this.country.name = this.countries[index].name
          found = true
        }
        index++
      }
    },
    getStateSelect () {
      var index = 0
      var found = false
      while (!found && index < this.ustates.length) {
        if (this.ustates[index].code === this.value.state) {
          this.state.code = this.ustates[index].code
          this.state.name = this.ustates[index].name
          found = true
        }
        index++
      }
    },
    translateCountries () {
      this.countries = JSON.parse(JSON.stringify(extraConstants.COUNTRIESLIST))
      this.countries.forEach(country => {
        country.name = this.$t(country.name)
      })
    }
  }
}
</script>

<style lang="css">
  .multiselect__tags {
    min-height: 44px !important;
  }
</style>
